// Copyright (c) 2023 WiseTime. All rights reserved.

import { concat, EMPTY, forkJoin, Observable, of } from "rxjs"
import { flatten } from "ramda"
import { Action } from "redux"
import { catchError, filter, mergeMap } from "rxjs/operators"
import { t } from "@lingui/macro"
import * as TimelineSlice from "../../timeline/slices/timeline"
import * as TagsActions from "../actions"
import * as TagsSlice from "../slice"
import { IdsList } from "../tags"
import { tagServiceClient } from "../../../app/rpc/client"
import { grpc } from "../../../app/rpc/executor"
import { showSnackBar } from "../../snackbar/actions"
import { getTagCategoryIdsList } from "./getTagCategoryIdsList"
import {
  BatchGetTagCategoriesRequest,
  BatchGetTagCategoriesResponse,
  BatchListTagsByCategoriesRequest,
  BatchListTagsByCategoriesResponse,
} from "../../../generated/legebill/site/tag/v4/tag_pb"

type BatchGetTagsByCategoriesRpc = (tagCategoryIdsList: IdsList) => Observable<BatchGetTagCategoriesResponse>
type BatchListTagsByCategoriesRpc = (tagCategoryIdsList: IdsList) => Observable<BatchListTagsByCategoriesResponse>

export const batchGetTagCategories = (
  batchGetTagCategoriesRpc: BatchGetTagsByCategoriesRpc,
  batchListTagsByCategoriesRpc: BatchListTagsByCategoriesRpc,
) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(action => (
        TagsActions.batchGetTagCategories.match(action)
        || TimelineSlice.setTimeline.match(action)
        || TimelineSlice.setTimeRowsForDates.match(action)
      )),
      mergeMap((action) => {
        const tagCategoryIdsList = getTagCategoryIdsList(action)
        if (tagCategoryIdsList.length === 0) {
          return EMPTY
        }
        return forkJoin([
          batchGetTagCategoriesRpc(tagCategoryIdsList),
          batchListTagsByCategoriesRpc(tagCategoryIdsList),
        ]).pipe(
          mergeMap(([batchGetTagCategoriesResponse, batchListTagsByCategoriesResponse]) => concat(
            of(TagsSlice.setTagCategories(batchGetTagCategoriesResponse.toObject().tagCategoriesList)),
            of(TagsSlice.setTags(flatten(
              batchListTagsByCategoriesResponse.toObject().tagsByCategoriesList.map(t => t.tagsList)
            ))),
          )),
          catchError(_ => of(showSnackBar({
            message: t({
              id: "msg_batch_get_tag_categories_failed",
              message: "Failed to fetch tag category details. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com.",
            }),
            messageType: "ERROR",
          }))),
        )
      }),
    )

const batchGetTagCategoriesRpc = (tagCategoryIdsList: IdsList): Observable<BatchGetTagCategoriesResponse> =>
  grpc(() => {
    return tagServiceClient.batchGetTagCategories(
      new BatchGetTagCategoriesRequest().setTagCategoryIdsList(tagCategoryIdsList),
      null,
    )
  })

const batchListTagsByCategoriesRpc = (tagCategoryIdsList: IdsList): Observable<BatchListTagsByCategoriesResponse> =>
  grpc(() => {
    return tagServiceClient.batchListTagsByCategories(
      new BatchListTagsByCategoriesRequest().setTagCategoryIdsList(tagCategoryIdsList),
      null,
    )
  })

export default batchGetTagCategories(batchGetTagCategoriesRpc, batchListTagsByCategoriesRpc)
